import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './auth-context';

// Export useApi for direct API access
export function useApi() {
  const { api, isConnected } = useAuth();
  if (!isConnected || !api) {
    throw new Error('API not connected');
  }
  return api;
}
import type {
  DashboardStats,
  ThreatFeedItem,
  Session,
  Fingerprint,
  Deception,
  DeceptionInteraction,
  EngagementFunnel,
  InteractionTypeBreakdown,
  ThreatAssessment,
  StixBundle,
  Agent,
  IntelStats,
  AttackerProfile,
  Tool,
  ThreatActor,
} from './api';

export interface RecommendedActions {
  summary: {
    totalThreatsBlocked: number;
    activeTechniques: number;
  };
  topIpsToBlock: Array<{
    ip: string;
    confidence: number;
    sessions: number;
    techniques: string[];
    country?: string;
    city?: string;
  }>;
  firewallRules: Array<{
    ip: string;
    rule: string;
    cloudflareRule: string;
    reason: string;
  }>;
  criticalTechniques: Array<{
    technique: string;
    occurrences: number;
  }>;
}

interface UseQueryResult<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Tenant-aware data fetch.
 * - Clears previous data when deps change (org switch must not flash wrong tenant)
 * - Ignores stale responses via generation counter (race when switching mid-poll)
 */
function useApiQuery<T>(
  fetcher: () => Promise<T> | undefined,
  deps: unknown[] = [],
  refreshIntervalMs?: number
): UseQueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const genRef = useRef(0);

  // Reset cached rows whenever tenant / query identity changes
  useEffect(() => {
    genRef.current += 1;
    setData(null);
    setError(null);
    setIsLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const fetchData = useCallback(() => {
    const promise = fetcher();
    if (!promise) {
      setIsLoading(false);
      return;
    }
    const gen = genRef.current;
    setIsLoading(true);
    promise
      .then((result) => {
        if (gen !== genRef.current) return; // stale (tenant switched)
        setData(result);
        setError(null);
      })
      .catch((err) => {
        if (gen !== genRef.current) return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        if (gen !== genRef.current) return;
        setIsLoading(false);
      });
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
    if (refreshIntervalMs && refreshIntervalMs > 0) {
      const interval = setInterval(fetchData, refreshIntervalMs);
      return () => clearInterval(interval);
    }
  }, [fetchData, refreshIntervalMs]);

  return { data, isLoading, error, refetch: fetchData };
}

/** All dashboard queries must depend on activeTenantId so org switch refetches. */
function useTenantQueryScope() {
  const { api, isConnected, activeTenantId, tenant } = useAuth();
  return {
    api,
    isConnected,
    /** Prefer explicit id; fall back to JWT tenant id */
    tenantKey: activeTenantId || tenant?.id || null,
  };
}

// Dashboard overview stats — refresh every 10s
export function useDashboardStats(hours?: number) {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<DashboardStats>(
    () => (isConnected && api ? api.getStats(hours) : undefined),
    [api, isConnected, hours, tenantKey],
    10000
  );
}

// Live threat feed — refresh every 5s
export function useThreatFeed() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ threats: ThreatFeedItem[] }>(
    () => (isConnected && api ? api.getThreatFeed() : undefined),
    [api, isConnected, tenantKey],
    5000
  );
}

// Sessions
export function useSessions(active?: boolean) {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ sessions: Session[] }>(
    () => (isConnected && api ? api.getSessions(active) : undefined),
    [api, isConnected, active, tenantKey],
    15000
  );
}

// Fingerprints
export function useFingerprints() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ fingerprints: Fingerprint[] }>(
    () => (isConnected && api ? api.getFingerprints() : undefined),
    [api, isConnected, tenantKey],
    30000
  );
}

// Actor clusters (IP+15m+soft UA) — set includeSynthetic to see lab/agent runs
export function useActors(includeSynthetic = false) {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ actors: import('./api').ActorCluster[]; total: number }>(
    () =>
      isConnected && api
        ? api.getActors({ includeSynthetic, limit: 100 })
        : undefined,
    [api, isConnected, includeSynthetic, tenantKey],
    15000
  );
}

// Deceptions
export function useDeceptions() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ deceptions: Deception[] }>(
    () => (isConnected && api ? api.getDeceptions() : undefined),
    [api, isConnected, tenantKey],
    10000
  );
}

// Deception Interactions (engagement timeline)
export function useDeceptionInteractions(options?: { sessionId?: string; deceptionId?: string; type?: string; limit?: number }) {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ interactions: DeceptionInteraction[]; funnel: EngagementFunnel; typeBreakdown: InteractionTypeBreakdown[] }>(
    () => (isConnected && api ? api.getDeceptionInteractions(options) : undefined),
    [api, isConnected, options?.sessionId, options?.deceptionId, options?.type, options?.limit, tenantKey],
    10000
  );
}

// Threats
export function useThreats() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ threats: ThreatAssessment[] }>(
    () => (isConnected && api ? api.getThreats() : undefined),
    [api, isConnected, tenantKey],
    10000
  );
}

// STIX bundles
export function useStixBundles() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ bundles: StixBundle[] }>(
    () => (isConnected && api ? api.getStixBundles() : undefined),
    [api, isConnected, tenantKey],
    60000
  );
}

// Prompt-injection canary hits
export function useCanaryHits(limit = 50) {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery(
    () => (isConnected && api ? api.getCanaryHits(limit) : undefined),
    [api, isConnected, limit, tenantKey],
    15000
  );
}

export function useCanaryHitDetail(id: string | null) {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery(
    () => (isConnected && api && id ? api.getCanaryHitDetail(id) : undefined),
    [api, isConnected, id, tenantKey],
    0
  );
}

// Agents
export function useAgents() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ agents: Agent[] }>(
    () => (isConnected && api ? api.getAgents() : undefined),
    [api, isConnected, tenantKey],
    15000
  );
}

// Fleet liveness + integrity (poll frequently — measurement-run critical)
export function useFleetStatus() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery(
    () => (isConnected && api ? api.getFleetStatus() : undefined),
    [api, isConnected, tenantKey],
    15000
  );
}

// Intelligence Stats
export function useIntelStats() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<IntelStats>(
    () => (isConnected && api ? api.getIntelStats() : undefined),
    [api, isConnected, tenantKey],
    10000
  );
}

// Attacker Profiles
export function useAttackerProfiles(minConfidence = 50, limit = 50) {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ profiles: AttackerProfile[]; total: number }>(
    () => (isConnected && api ? api.getAttackerProfiles(minConfidence, limit) : undefined),
    [api, isConnected, minConfidence, limit, tenantKey],
    15000
  );
}

// Detected Tools
export function useDetectedTools() {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ tools: Tool[]; total_unique: number; total_detections: number; profiles_analyzed: number }>(
    () => (isConnected && api ? api.getDetectedTools() : undefined),
    [api, isConnected, tenantKey],
    30000
  );
}

// Threat Actors
export function useThreatActors(limit = 50) {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<{ threat_actors: ThreatActor[]; total: number }>(
    () => (isConnected && api ? api.getThreatActors(limit) : undefined),
    [api, isConnected, limit, tenantKey],
    30000
  );
}

// Recommended Actions — derive from threats + sessions
export function useRecommendedActions(): UseQueryResult<RecommendedActions> {
  const { api, isConnected, tenantKey } = useTenantQueryScope();
  return useApiQuery<RecommendedActions>(
    async () => {
      if (!isConnected || !api) return undefined as unknown as RecommendedActions;
      const [threatsRes, sessionsRes] = await Promise.all([
        api.getThreats(),
        api.getSessions(true).catch(() => ({ sessions: [] })),
      ]);

      const high = threatsRes.threats.filter(t => t.level === 'high' || t.level === 'critical');

      // Aggregate by IP from sessions that match high/critical threat sessions
      const sessionById = new Map(sessionsRes.sessions.map(s => [s.id, s] as const));
      const byIp: Record<string, { confidence: number; sessions: number; techniques: Set<string>; city?: string; country?: string }> = {};
      for (const t of high) {
        const s = sessionById.get(t.sessionId);
        if (!s) continue;
        if (!byIp[s.sourceIp]) {
          byIp[s.sourceIp] = { confidence: 0, sessions: 0, techniques: new Set(), city: s.sourceCity, country: s.sourceCountry };
        }
        byIp[s.sourceIp].confidence = Math.max(byIp[s.sourceIp].confidence, t.confidence);
        byIp[s.sourceIp].sessions += 1;
        t.techniques.forEach(tech => byIp[s.sourceIp].techniques.add(tech));
      }

      const topIps = Object.entries(byIp)
        .map(([ip, v]) => ({ ip, confidence: v.confidence, sessions: v.sessions, techniques: Array.from(v.techniques).slice(0, 4), city: v.city, country: v.country }))
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 10);

      const firewallRules = topIps.map(({ ip, confidence }) => ({
        ip,
        rule: `iptables -A INPUT -s ${ip} -j DROP`,
        cloudflareRule: `(ip.src eq ${ip})`,
        reason: `High risk (${confidence} confidence)`,
      }));

      // Technique frequency
      const techMap: Record<string, number> = {};
      for (const t of high) t.techniques.forEach(tech => { techMap[tech] = (techMap[tech] || 0) + 1; });
      const criticalTechniques = Object.entries(techMap)
        .map(([technique, occurrences]) => ({ technique, occurrences }))
        .sort((a, b) => b.occurrences - a.occurrences)
        .slice(0, 10);

      const summary = {
        totalThreatsBlocked: topIps.length,
        activeTechniques: criticalTechniques.length,
      };

      return { summary, topIpsToBlock: topIps, firewallRules, criticalTechniques } as RecommendedActions;
    },
    [api, isConnected, tenantKey],
    15000
  );
}
